import { yupResolver } from "@hookform/resolvers/yup";
import { updateFinanceDeclaration } from "api/compliance";
import { IComplianceInfo } from "api/compliance/types";
import { IError } from "api/types";
import Button from "components/InvestorCommon/Button/Button";
import SelectBox from "components/InvestorCommon/SelectV2/Select";
import { FC, useEffect, useMemo, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import Select, { StylesConfig } from "react-select";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import {
	CorporateInvestorClassificationsMap,
	areCorporateClassificationIdsActive,
	getSelectedInvestorClassificationOptions,
	InvestorClassificationOption,
} from "pages/InvestorPage/Corporate/CompanyInfo/components/Declaration/utils/investorClassification";
import { useInvestorClassificationRequirementContext } from "state/investorClassificationRequirement";
import {
	hasSelectedInactiveProfileCorporateClassifications,
	getCorporateRequirementClassificationOptions,
	INACTIVE_CLASSIFICATION_ERROR,
} from "utils/corporateInvestorClassification";
import { escapeMetadataValue } from "utils/sanitizeMetadata";
import * as yup from "yup";
import { ClassificationFormProps } from "./IndividualInvestorClassificationForm";
import { Actions, FormGrid } from "./Styled";

type CorporateFormValues = {
	investorType: string;
	investorClassification: number[];
};

const investorClassificationSelectStyles: StylesConfig<InvestorClassificationOption, true> = {
	control: (baseStyles) => ({
		...baseStyles,
		marginTop: "8px",
		minHeight: "50px",
		alignItems: "center",
	}),
	valueContainer: (baseStyles) => ({
		...baseStyles,
		flexWrap: "wrap",
		alignItems: "center",
		gap: 4,
		paddingTop: 4,
		paddingBottom: 4,
	}),
	multiValue: (baseStyles) => ({
		...baseStyles,
		maxWidth: "calc(100% - 44px)",
		margin: 0,
	}),
	multiValueLabel: (baseStyles) => ({
		...baseStyles,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	}),
	input: (baseStyles) => ({
		...baseStyles,
		margin: 0,
		padding: 0,
		flex: "0 0 auto",
		width: "auto",
		minWidth: 2,
		"input:focus": {
			boxShadow: "none",
		},
	}),
	indicatorsContainer: (baseStyles) => ({
		...baseStyles,
		alignSelf: "center",
	}),
	placeholder: (baseStyles) => ({
		...baseStyles,
		margin: 0,
	}),
};

const createCorporateSchema = (
	classifications: CorporateInvestorClassificationsMap,
	compliance?: IComplianceInfo | null,
) =>
	yup.object().shape({
		investorType: yup.string().required("Investor Type is required."),
		investorClassification: yup
			.array()
			.test(
				"not-empty",
				"Investor Classification is required.",
				(value: number[] = []) => value.length > 0,
			)
			.test(
				"active-classification",
				INACTIVE_CLASSIFICATION_ERROR,
				function (value: number[] = []) {
					if (!value.length) return true;

					if (hasSelectedInactiveProfileCorporateClassifications(value, compliance)) {
						return false;
					}

					const selectedInvestorType = this.parent.investorType as string | undefined;
					return areCorporateClassificationIdsActive(
						value,
						selectedInvestorType,
						classifications,
					);
				},
			),
	});

const SELECT_BASED_STYLES = {
	input: (_provided: Record<string, unknown>) => ({
		"input:focus": {
			boxShadow: "none",
		},
	}),
};

const CorporateInvestorClassificationForm: FC<ClassificationFormProps> = ({
	onClose,
	onCompleted,
	isSubmitting,
	setIsSubmitting,
}) => {
	const submitRef = useRef<HTMLButtonElement>(null);
	const { complianceInfo, corporateInvestorClassifications, corporateMetadataOptions } =
		useInvestorClassificationRequirementContext();
	const validationSchema = useMemo(
		() => createCorporateSchema(corporateInvestorClassifications, complianceInfo),
		[corporateInvestorClassifications, complianceInfo],
	);
	const previousInvestorTypeRef = useRef<string | undefined>(
		complianceInfo?.investorType ?? undefined,
	);
	const initialFormValues = useMemo(
		(): CorporateFormValues => ({
			investorType: complianceInfo?.investorType ?? "",
			investorClassification: (complianceInfo?.investorClassificationCriteriaIds ?? []).map(
				(id) => Number(id),
			),
		}),
		[complianceInfo],
	);

	const {
		handleSubmit,
		register,
		control,
		reset,
		watch,
		setError,
		setValue,
		trigger,
		formState: { errors },
	} = useForm<CorporateFormValues>({
		resolver: yupResolver(validationSchema),
		defaultValues: initialFormValues,
		mode: "onChange",
	});

	const watchedInvestorType = watch("investorType");
	const classificationOptions = useMemo(
		() =>
			getCorporateRequirementClassificationOptions(
				watchedInvestorType,
				corporateInvestorClassifications,
				complianceInfo,
			),
		[watchedInvestorType, corporateInvestorClassifications, complianceInfo],
	);
	const investorTypesOptions = corporateMetadataOptions?.investorTypes ?? [];

	useEffect(() => {
		if (!complianceInfo) return;

		const investorType = complianceInfo.investorType ?? "";
		const investorClassification = (complianceInfo.investorClassificationCriteriaIds ?? []).map(
			(id) => Number(id),
		);

		reset({
			investorType,
			investorClassification,
		});
		previousInvestorTypeRef.current = investorType;

		void trigger("investorClassification");
	}, [complianceInfo, reset, trigger]);

	const handleInvestorTypeChange = (nextInvestorType: string) => {
		const previousInvestorType = previousInvestorTypeRef.current ?? "";
		const normalizedNextInvestorType = nextInvestorType ?? "";

		if (previousInvestorType !== normalizedNextInvestorType) {
			setValue("investorClassification", [], { shouldValidate: true });
		}

		previousInvestorTypeRef.current = normalizedNextInvestorType || undefined;
	};

	const [submitRequirement] = useMutation(updateFinanceDeclaration, {
		onSuccess: () => {
			toast.success("Your changes has been saved successfully!");
			onCompleted();
		},
		onError: (error: IError) => {
			setIsSubmitting(false);
			const message = Array.isArray(error?.message) ? error.message[0] : error?.message;

			if (message === INACTIVE_CLASSIFICATION_ERROR) {
				setError("investorClassification", {
					type: "server",
					message: INACTIVE_CLASSIFICATION_ERROR,
				});
				return;
			}

			toast.error(message || "Unable to save changes. Please try again.");
		},
	});

	const buildCorporateSubmitPayload = (values: CorporateFormValues) => ({
		sourceOfFunds: complianceInfo?.sourceOfFundsWealth ?? [],
		investmentObjective: complianceInfo?.investmentObjective ?? [],
		annualRevenue: escapeMetadataValue(complianceInfo?.annualRevenue ?? ""),
		investmentDuration: escapeMetadataValue(complianceInfo?.investmentDuration ?? ""),
		totalNetAssets: escapeMetadataValue(complianceInfo?.totalNetAssets ?? ""),
		investorType: values.investorType,
		investorClassificationCriteriaIds: values.investorClassification,
		annualAnticipatedInvestmentAmount: escapeMetadataValue(
			complianceInfo?.annualAnticipatedInvestmentAmount ?? "",
		),
		isTaxResident: Boolean(complianceInfo?.isTaxResident),
	});

	const onSubmit = (values: CorporateFormValues) => {
		setIsSubmitting(true);
		submitRequirement(buildCorporateSubmitPayload(values));
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<FormGrid>
				<SelectBox
					title={
						<div className="text-base">
							Investor Type <span className="text-red-500">*</span>
						</div>
					}
					register={register}
					control={control}
					propertiesName="investorType"
					styles={SELECT_BASED_STYLES}
					data={investorTypesOptions}
					errorMessage={errors.investorType?.message}
					handleChangeValue={handleInvestorTypeChange}
				/>
				<div className="flex flex-col">
					<div className="text-base mb-[2px]">
						Investor Classification <span className="text-red-500">*</span>
					</div>
					<Controller
						control={control}
						name="investorClassification"
						render={({ field: { onChange, value, ref } }) => {
							const selectedOptions = getSelectedInvestorClassificationOptions(
								value ?? [],
								watchedInvestorType,
								corporateInvestorClassifications,
								complianceInfo?.investorClassificationCriteriaLabels ?? [],
								complianceInfo?.investorClassificationCriteriaIds ?? [],
							);

							return (
								<Select
									styles={investorClassificationSelectStyles}
									closeMenuOnSelect={false}
									hideSelectedOptions={false}
									menuPlacement="auto"
									isMulti
									isSearchable={false}
									components={{ Input: () => null }}
									isDisabled={!watchedInvestorType}
									placeholder={
										watchedInvestorType
											? "Select one or more options"
											: "Select Investor Type first"
									}
									value={selectedOptions}
									onChange={(selectedOptions) => {
										onChange(
											(selectedOptions ?? []).map((option) =>
												Number(option.value),
											),
										);
									}}
									inputRef={ref}
									options={classificationOptions}
									isOptionDisabled={(option) => Boolean(option.isDisabled)}
									getOptionValue={(option) => option.value}
									getOptionLabel={(option) => option.label}
								/>
							);
						}}
					/>
					<div
						style={{
							color: "#EA3F30",
							font: "normal normal bold 14px/18px Lato",
							marginTop: "5px",
						}}
					>
						{errors.investorClassification?.message}
					</div>
				</div>
			</FormGrid>

			<Actions>
				<Button
					type="button"
					theme="primary"
					size="small"
					disabled={isSubmitting}
					onClick={onClose}
				>
					Cancel
				</Button>
				<Button
					ref={submitRef}
					type="submit"
					theme="secondary"
					size="small"
					disabled={isSubmitting}
				>
					Submit
				</Button>
			</Actions>
		</form>
	);
};

export default CorporateInvestorClassificationForm;
